import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { errorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';
import { homeFor } from '@/app/roleNav';
import { AuthLayout } from './AuthLayout';
import { MfaChallenge } from './MfaChallenge';

interface Challenge {
  token: string;
  enrollmentRequired: boolean;
}

export function LoginPage() {
  const { signIn, adoptToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<Challenge | null>(null);

  function goHome(role: Parameters<typeof homeFor>[0]) {
    // Honour a deep link the guard stashed, otherwise send them to their own home.
    navigate(params.get('next') ?? homeFor(role), { replace: true });
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await signIn(email, password);
      if (result.kind === 'mfa') {
        // No session exists yet. The password step is done; the code step is not.
        setChallenge({
          token: result.challengeToken,
          enrollmentRequired: result.enrollmentRequired,
        });
        setBusy(false);
        return;
      }
      goHome(result.user.role);
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  async function completeChallenge(accessToken: string) {
    try {
      const user = await adoptToken(accessToken);
      goHome(user.role);
    } catch (caught) {
      setError(errorMessage(caught));
      setChallenge(null);
    }
  }

  if (challenge) {
    return (
      <AuthLayout
        title={challenge.enrollmentRequired ? 'Set up two-factor' : 'Two-factor authentication'}
        subtitle={
          challenge.enrollmentRequired
            ? 'This account can provision devices and read every customer, so it needs a second factor.'
            : 'One more step to confirm it is you.'
        }
      >
        <MfaChallenge
          challengeToken={challenge.token}
          enrollmentRequired={challenge.enrollmentRequired}
          onAuthenticated={completeChallenge}
          onCancel={() => {
            setChallenge(null);
            setPassword('');
          }}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Access your vehicles, devices and alert history."
      footer={
        <>
          New here?{' '}
          <Link to="/signup" className="font-medium text-accent hover:text-accent-hover">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          autoFocus
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <div>
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <div className="mt-1.5 text-right">
            <Link
              to="/forgot-password"
              className="text-[13px] font-medium text-accent hover:text-accent-hover"
            >
              Forgot your password?
            </Link>
          </div>
        </div>

        {error && (
          <p className="rounded-[--radius-control] bg-bad-soft px-3 py-2 text-[13px] text-bad" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" loading={busy} className="w-full">
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
